import { useForm } from 'react-hook-form'

import { useUnitControlProgram } from '@apps-common/apis/portal/control'
import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { AccountReservationInputSM, FieldInfoMessage } from '@apps-common/components/elements/form'
import { VertSpacerLg } from '@apps-common/components/layout'
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { MessageProgramControl } from '@apps-common/config/app-messages'
import { AppType } from '@apps-common/config/app-types'
import { CONTROL_STATE_ON, ControlProgramCommand } from '@apps-common/config/controls'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_STATUS_ACTIVE } from '@apps-common/config/unit-filter'
import { GarbageManualProvider } from '@apps-common/contexts/GarbageManualContext'
import { invalidateAllUnitQueries } from '@apps-common/helpers/invalidate-unit-queries'
import { toastError, toastSuccess } from '@apps-common/lib/toast'

interface IAccountChangeForm {
  accountNo: string
  reservedAccountId: number
}

export interface IAccountChangeModalProps {
  unitId: string
  caller: AppType
}

export const AccountChangeModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IAccountChangeModalProps
}) => {
  const { unitId, caller = AppType.portal } = componentProps
  const {
    data: unit,
    isLoading: unitIsLoading,
    isError: unitIsError
  } = useGetUnitGenericDetails({ status: UNIT_STATUS_ACTIVE.value as string, unitId })

  const { mutate: programUnit, isPending: isProgrammingUnit } = useUnitControlProgram(
    true,
    () => {
      invalidateAllUnitQueries(unitId, caller)
      onDismiss(null, ModalDismiss.Confirm)
      toastSuccess(MessageProgramControl.AccountChangeSuccess)
    },
    () => {
      toastError(MessageProgramControl.AccountChangeFailure)
    }
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<IAccountChangeForm>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const submit = (values: IAccountChangeForm) =>
    programUnit({
      model: {
        unitId: unit?.unitId || '',
        triggerType: '',
        onTime: 0,
        commands: [
          {
            id: 0,
            unitControlId: 0,
            commandState: CONTROL_STATE_ON,
            command: ControlProgramCommand.AccountNumber,
            value: values.accountNo
          }
        ]
      },
      reservedId: values.reservedAccountId
    })

  const loading = unitIsLoading || unitIsError
  const updating = isProgrammingUnit

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0 !pb-0'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Device config' title={`Account change`} />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px] !pb-[50px] sm:!pb-[20px]'>
        <GarbageManualProvider>
          <form noValidate onSubmit={handleSubmit(submit)}>
            <div className='bg-warning border border-on-warning px-4 py-2 rounded-3xl'>
              <FieldInfoMessage
                message={`Proceeding will update this device’s account number from ${unit?.accountNo} to the account number provided below.`}
                classes='!text-on-warning !text-sm'
              />
            </div>
            <VertSpacerLg />
            <AccountReservationInputSM
              id='accountNo'
              placeholder='Enter new CID account number'
              label='Account number'
              showLabel
              containerClassName=''
              loading={loading}
              disabled={updating}
              existingAccountNo=''
              releaseOnDispose={() => true}
              registration={register('accountNo', {
                required: {
                  value: true,
                  message: 'Required'
                }
              })}
              mCentreId={unit?.mCentreId}
              onError={() => {
                setValue('reservedAccountId', NaN, { shouldValidate: true })
              }}
              onSuccess={(data) => {
                setValue('reservedAccountId', data.id, { shouldValidate: true })
                setValue('accountNo', data.accountNo, { shouldValidate: true })
              }}
              onReset={() => {
                setValue('reservedAccountId', NaN, { shouldValidate: true })
                setValue('accountNo', '', { shouldValidate: true })
              }}
              errors={errors?.accountNo}
            />
            <VertSpacerLg />
            <div className='flex items-center justify-start gap-[10px]'>
              <ButtonRoundedSm type='submit' loading={loading || updating} text='Proceed' className='action-warning sm:grow-0' />
              <ButtonRoundedSm
                type='button'
                text='Cancel'
                loading={loading || updating}
                callback={() => onDismiss(null, 'cancel')}
                className='action-neutral sm:grow-0'
              />
            </div>
          </form>
        </GarbageManualProvider>
      </ModalBody>
    </ModalContainer>
  )
}
